import { createForm, type RenderParams } from '@seek/forms-ui';
import {
  ClassificationDropdown,
  FormActionButtons,
} from '@seek/profile-edit-ui';
import { rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';

import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { TaxonomyOptionsErrorAlert } from 'src/components/Profile/TaxonomyOptionsErrorAlert/TaxonomyOptionsErrorAlert';
import { CLASSIFICATION } from 'src/components/Router';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';
import { NEXT_ROLE_SECTION_CONTENT_SPACING } from 'src/styles';

import {
  useGetClassificationOptions,
  usePreferredClassification,
  useUpdatePreferredClassification,
} from '../data';

import translations from './.vocab';

const CLASSIFICATION_FORM = 'classification-form';

interface ClassificationValue {
  id?: number;
  subClassification?: {
    id: number;
  };
}

const mapFormStateToProps = ({
  form: { dirty: isFormDirty },
  values: { classification },
}: {
  form: {
    dirty: boolean;
  };
  values: { classification: ClassificationValue };
}) => ({
  isFormDirty,
  classification,
});

type MappedProps = ReturnType<typeof mapFormStateToProps>;

interface FormValues {
  classification: ClassificationValue;
}

type FormProps = RenderParams<FormValues> & MappedProps;

const { FormProvider, useField } = createForm<FormValues, MappedProps>(
  CLASSIFICATION_FORM,
  {
    formType: 'braid',
  },
);

const EditClassificationForm = ({
  classification,
  isFormDirty,
  handleSubmit,
  onFormDirtyChange,
}: FormProps & DirtyFormProps) => {
  const { t } = useTranslations(translations);

  const { zone } = useAppConfig();
  const {
    loading,
    getSubClassifications,
    error: classificationOptionsError,
    refetch: refetchGetClassificationOptions,
  } = useGetClassificationOptions();
  const enterReadMode = useEnterReadMode();

  useOnFormDirtyChange({ onFormDirtyChange, dirty: isFormDirty });

  const { valid, ...classificationField } = useField({
    id: 'classification',
    ...rules.classification({
      secondaryData: classification?.id
        ? getSubClassifications(classification.id)
        : [],
    }),
  });

  const [updatePreferredClassification] = useUpdatePreferredClassification();

  const handleOnSubmit = () => {
    const {
      value: { id: classificationId, subClassification },
    } = classificationField;

    if (isFormDirty && classificationId && subClassification) {
      updatePreferredClassification({
        input: {
          classification: {
            id: classificationId,
            subClassification,
          },
        },
      });
    }
    enterReadMode();
  };

  const classificationRef = useFocus<HTMLSelectElement>({
    card: CLASSIFICATION,
    fieldName: 'classification',
    domReady: !loading,
  });

  return (
    <Stack space={NEXT_ROLE_SECTION_CONTENT_SPACING}>
      {classificationOptionsError ? (
        <TaxonomyOptionsErrorAlert refetch={refetchGetClassificationOptions} />
      ) : (
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <Stack space={NEXT_ROLE_SECTION_CONTENT_SPACING}>
            <ClassificationDropdown
              id="classification"
              ref={classificationRef}
              {...classificationField}
              valid={valid}
              zone={zone}
            />
            <FormActionButtons
              cardName={t('Classification of interest')}
              dataAutomationSuffix="classification"
              onCancel={enterReadMode}
            />
          </Stack>
        </form>
      )}
    </Stack>
  );
};

export const EditClassification = (dirtyFormProps: DirtyFormProps) => {
  const { preferredClassification } = usePreferredClassification();
  const { language } = useAppConfig();

  return (
    <FormProvider
      mapFormStateToProps={mapFormStateToProps}
      initialValues={{ classification: preferredClassification || {} }}
      language={language}
    >
      {(formProps) => (
        <EditClassificationForm {...dirtyFormProps} {...formProps} />
      )}
    </FormProvider>
  );
};
